import { FaFacebook, FaInstagram, FaLinkedin, FaReddit, FaTiktok, FaYoutube } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import BuiltByCredit from '../../../components/BuiltByCredit';
import { ThemeToggle } from '../../../components/theme/ThemeToggle';
import { trackUserEvent } from '../../../utils/reddit-pixel';

const socialLinks = [
  { icon: FaInstagram, url: 'https://instagram.com/empatheaseapp', label: 'Instagram' },
  { icon: FaTiktok, url: 'https://tiktok.com/@empy871', label: 'TikTok' },
  { icon: FaYoutube, url: 'https://www.youtube.com/@EmpatheaseTalks', label: 'YouTube' },
  { icon: FaFacebook, url: 'https://www.facebook.com/empathease.talks/', label: 'Facebook' },
  {
    icon: FaLinkedin,
    url: 'https://www.linkedin.com/in/empathease-talks-126933359',
    label: 'LinkedIn',
  },
  { icon: FaReddit, url: 'https://www.reddit.com/r/empathease/', label: 'Reddit' },
];

const productLinks = [
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Features', href: '#features' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'FAQ', href: '#faq' },
];

const resourceLinks: { label: string; to?: string; href?: string; onClick?: () => void }[] = [
  { label: 'Blog', to: '/learn' },
  {
    label: 'Take the Quiz',
    to: '/quiz-assessment',
    onClick: () => trackUserEvent('Custom', { customEventName: 'ClickQuizCTA' }),
  },
  {
    label: 'Start Free',
    to: '/register',
    onClick: () => trackUserEvent('Custom', { customEventName: 'ClickStartFree' }),
  },
  { label: 'Login', to: '/login' },
];

const legalLinks = [
  { label: 'Privacy Policy', to: '/privacy' },
  { label: 'Terms of Use', to: '/terms-of-use' },
  { label: 'Contact', href: 'mailto:hello@empathease.app' },
];

export default function LandingFooter() {
  const navigate = useNavigate();

  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const sectionId = href.slice(1);
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    } else {
      void navigate(`/?scrollTo=${sectionId}`);
    }
  };

  return (
    <footer className="ee-landing-footer">
      <div className="ee-landing-container">
        <div className="ee-landing-footer__grid">
          {/* Brand */}
          <div className="ee-landing-footer__brand-col">
            <Link to="/" className="ee-landing-footer__brand" aria-label="EmpathEase Home">
              <img src="/logo.webp" alt="EmpathEase" className="ee-landing-footer__logo" />
              <span className="ee-landing-footer__word">EmpathEase</span>
            </Link>
            <p className="ee-landing-footer__tagline">
              Learn the communication skills that transform relationships.
            </p>
          </div>

          {/* Product */}
          <div>
            <h3 className="ee-landing-footer__heading">Product</h3>
            <ul className="ee-landing-footer__list">
              {productLinks.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    onClick={(e) => handleAnchorClick(e, link.href)}
                    className="ee-landing-footer__link"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="ee-landing-footer__heading">Get Started</h3>
            <ul className="ee-landing-footer__list">
              {resourceLinks.map((link) => (
                <li key={link.label}>
                  {link.to ? (
                    <Link to={link.to} onClick={link.onClick} className="ee-landing-footer__link">
                      {link.label}
                    </Link>
                  ) : (
                    <a href={link.href} onClick={link.onClick} className="ee-landing-footer__link">
                      {link.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="ee-landing-footer__heading">Legal</h3>
            <ul className="ee-landing-footer__list">
              {legalLinks.map((link) => (
                <li key={link.label}>
                  {link.to ? (
                    <Link to={link.to} className="ee-landing-footer__link">
                      {link.label}
                    </Link>
                  ) : (
                    <a href={link.href} className="ee-landing-footer__link">
                      {link.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Social */}
          <div>
            <h3 className="ee-landing-footer__heading">Connect</h3>
            <div className="ee-landing-footer__social">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ee-landing-footer__social-link"
                  aria-label={social.label}
                >
                  <social.icon aria-hidden="true" focusable="false" />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="ee-landing-footer__legal">
          <p className="ee-landing-footer__copy">
            &copy; {new Date().getFullYear()} EmpathEase. Made with love for connection.
            <BuiltByCredit />
          </p>
          <p className="ee-landing-footer__credit">
            Built on the work of Marshall B. Rosenberg (1934&ndash;2015), founder of Nonviolent
            Communication.
          </p>
          <ThemeToggle className="ee-landing-footer__theme" testId="theme-toggle-footer" />
        </div>
      </div>
    </footer>
  );
}
